// Small presentational primitives shared across activity renderers. Kept here
// (not in @fajrarabi/ui) because they're playful, lesson-specific, and depend
// on the render context (Tashkeel mode, CDN base).

import type { ReactElement, ReactNode } from 'react';

import { mediaUrl } from '../media';
import { stripTashkeel } from '../tashkeel';
import type { RenderContext } from './types';

// Render Arabic text honoring the learner's tashkeel mode. `<bdi>` isolates it
// so surrounding LTR chrome doesn't reorder it.
export function ArabicText({
  text,
  ctx,
  className,
}: {
  text: string;
  ctx: RenderContext;
  className?: string;
}): ReactElement {
  return <bdi className={className}>{stripTashkeel(text, ctx.tashkeelMode)}</bdi>;
}

// A play-audio button. No-ops gracefully when the clip is missing.
export function AudioButton({
  src,
  ctx,
  label,
}: {
  src: string | null | undefined;
  ctx: RenderContext;
  label: string;
}): ReactElement | null {
  const url = mediaUrl(ctx.cdnBase, src);
  if (!url) return null;
  return (
    <button
      type="button"
      onClick={() => {
        const audio = new Audio(url);
        void audio.play().catch(() => {
          /* autoplay/network failure is non-fatal */
        });
      }}
      className="inline-flex min-h-[56px] min-w-[56px] items-center justify-center rounded-full bg-sun-300 text-2xl text-burgundy-700 shadow-sm transition-transform hover:scale-105 active:scale-95"
      aria-label={label}
    >
      🔊
    </button>
  );
}

// A lesson illustration. Falls back to a soft emoji tile when no image.
export function ActivityImage({
  src,
  ctx,
  alt,
  fallbackEmoji = '🖼️',
}: {
  src: string | null | undefined;
  ctx: RenderContext;
  alt: string;
  fallbackEmoji?: string;
}): ReactElement {
  const url = mediaUrl(ctx.cdnBase, src);
  if (!url) {
    return (
      <span
        className="grid aspect-square w-full place-items-center rounded-3xl bg-cream-100 text-6xl"
        role="img"
        aria-label={alt}
      >
        {fallbackEmoji}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      className="aspect-square w-full rounded-3xl object-cover"
      loading="lazy"
    />
  );
}

// A large, accessible choice button. Reflects correct/incorrect once locked.
export function ChoiceButton({
  onClick,
  selected,
  state,
  disabled,
  children,
}: {
  onClick: () => void;
  selected: boolean;
  state: 'idle' | 'correct' | 'incorrect';
  disabled: boolean;
  children: ReactNode;
}): ReactElement {
  const base =
    'flex min-h-[64px] w-full items-center justify-center gap-3 rounded-2xl border-2 px-5 py-4 text-lg font-bold transition-colors';
  const skin =
    state === 'correct'
      ? 'border-leaf bg-leaf/15 text-burgundy-700'
      : state === 'incorrect'
        ? 'border-heart bg-heart/10 text-burgundy-700'
        : selected
          ? 'border-burgundy-500 bg-burgundy-50 text-burgundy-700'
          : 'border-cream-200 bg-white text-ink-900 hover:border-sun-300 hover:bg-sun-50';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`${base} ${skin} disabled:cursor-default`}
    >
      {children}
    </button>
  );
}
